import React, { useState, useMemo, useEffect, ReactElement } from 'react';
import clsx from 'clsx';
import { useAuth } from '../context/AuthContext';
import { PermMode } from './WithPermission';

interface SettingTabs {
  perms: string[];
  permsMode: PermMode;
  label: string;
  component: ReactElement;
}

export type TabSettings = Record<string, SettingTabs>;

interface Props {
  tabsSettings: TabSettings;
  onTabChange?: (v: string) => void;
  selectedTab?: string;
}

export const TabsWithPerms = ({ tabsSettings, onTabChange, selectedTab }: Props) => {
  const { checkPerm } = useAuth();

  const filteredTabs = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(tabsSettings).filter(([_, { perms, permsMode }]) => checkPerm(perms, permsMode))
      ),
    [checkPerm, tabsSettings]
  );

  const [activeTab, setActiveTab] = useState();

  const handleTabClick = (tabType: any) => () => {
    setActiveTab(tabType);
    onTabChange?.(tabType);
  };

  useEffect(() => {
    handleTabClick(selectedTab || Object.keys(filteredTabs)[0])();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="flex text-lg leading-5 mt-5 relative" data-testid="marketing-tabs">
        {Object.keys(filteredTabs).map((tabType: string) => {
          const { label } = filteredTabs[tabType];
          return (
            <button
              type="button"
              data-type={tabType}
              onClick={handleTabClick(tabType)}
              data-testid={`tab-button-${tabType}`}
              key={tabType}
              className={clsx(
                'sm:mr-2 mr-7 md:mr-7 p-3',
                activeTab === tabType
                  ? 'border-b-4 border-blue-80 text-black'
                  : 'rounded-lg hover:text-blue-80 hover:bg-pale-blue text-gray-60'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="mt-4 px-1 w-full">{activeTab && tabsSettings[activeTab]?.component}</div>
    </>
  );
};
